import axios, { AxiosError } from 'axios'
import * as Routes from '../../../routes'
import {
  CatalogueItem,
  CategoryNode,
  CategoryOption,
  NamedRecord,
  ProductAttributeOptions,
  ProductColumn,
  ProductRow,
  SelectOption,
  ServerErrors,
  toSelectOption
} from './catalogueTypes'
import { rootPositionsPayload, tablePositionsPayload } from './catalogueOrdering'
import { t } from '../../i18n'

const JSON_FORMAT = { format: 'json' }
const MULTIPART_HEADERS = { 'Content-Type': 'multipart/form-data' }

const toOptions = (records: NamedRecord[]): SelectOption[] => records.map(toSelectOption)

const getNamed = (url: string) => axios.get<NamedRecord[]>(url).then((response) => response.data)

export const fetchRoots = (): Promise<CategoryNode[]> =>
  axios.get<CategoryNode[]>(Routes.roots_product_categories_path(JSON_FORMAT)).then((response) => response.data)

export const fetchCategoryOptions = (): Promise<CategoryOption[]> =>
  axios
    .get<{ product_categories: CategoryOption[] }>(Routes.select_options_product_categories_path(JSON_FORMAT))
    .then((response) => response.data.product_categories)

export const fetchCategoryRows = (categoryId: number, signal?: AbortSignal): Promise<CatalogueItem[]> =>
  axios
    .get<CatalogueItem[]>(Routes.product_category_path(categoryId, JSON_FORMAT), { signal })
    .then((response) => response.data)

export const fetchProductColumns = (): Promise<ProductColumn[]> =>
  axios.get<ProductColumn[]>(Routes.product_columns_products_path(JSON_FORMAT)).then((response) => response.data)

export const fetchProductAttributes = (): Promise<ProductAttributeOptions> =>
  Promise.all([
    getNamed(Routes.units_product_attributes_path()),
    getNamed(Routes.alergens_product_attributes_path()),
    getNamed(Routes.fractions_product_attributes_path()),
    getNamed(Routes.admin_feeder_dosing_modes_path(JSON_FORMAT)),
    getNamed(Routes.admin_sauce_dosing_modes_path(JSON_FORMAT))
  ]).then(([units, alergens, fractions, feederDosingModes, sauceDosingModes]) => ({
    units: toOptions(units),
    alergens: toOptions(alergens),
    fractions: toOptions(fractions),
    feederDosingModes: toOptions(feederDosingModes),
    sauceDosingModes: toOptions(sauceDosingModes)
  }))

export const updateRootPositions = (roots: CategoryNode[]): Promise<unknown> =>
  axios.post(Routes.update_positions_product_categories_path(JSON_FORMAT), { categories: rootPositionsPayload(roots) })

export const updateTablePositions = (rows: CatalogueItem[]): Promise<unknown> =>
  axios.post(Routes.update_table_positions_product_categories_path(JSON_FORMAT), {
    categoryChildren: tablePositionsPayload(rows)
  })

export const updateProductColumns = (records: ProductColumn[]): Promise<unknown> =>
  axios.post(Routes.update_columns_dynamic_tables_path(JSON_FORMAT), { class_name: 'Product', records })

export const searchProducts = (name: string, categoryId: number | null, signal?: AbortSignal): Promise<ProductRow[]> =>
  axios
    .get<ProductRow[]>(Routes.search_products_path({ name, product_category_id: categoryId }), { signal })
    .then((response) => response.data)

export const saveProduct = (form: FormData, productId: number | null): Promise<unknown> =>
  axios({
    method: productId === null ? 'POST' : 'PUT',
    url: productId === null ? Routes.products_path(JSON_FORMAT) : Routes.product_path(productId, JSON_FORMAT),
    data: form,
    headers: MULTIPART_HEADERS
  })

export const saveCategory = (form: FormData, categoryId: number | null): Promise<unknown> =>
  axios({
    method: categoryId === null ? 'POST' : 'PUT',
    url:
      categoryId === null
        ? Routes.product_categories_path(JSON_FORMAT)
        : Routes.product_category_path(categoryId, JSON_FORMAT),
    data: form,
    headers: MULTIPART_HEADERS
  })

export const deleteItem = (item: CatalogueItem): Promise<unknown> =>
  axios.delete(item.class_name === 'Product' ? Routes.product_path(item.id) : Routes.product_category_path(item.id))

interface ErrorBody {
  full_errors?: ServerErrors
  base?: string[]
}

const errorBody = (error: unknown): ErrorBody => {
  if (!axios.isAxiosError(error)) return {}
  const data: unknown = (error as AxiosError).response?.data
  return typeof data === 'object' && data !== null ? (data as ErrorBody) : {}
}

export const serverErrors = (error: unknown): ServerErrors => errorBody(error).full_errors ?? {}

export const errorMessage = (error: unknown): string => {
  const messages = Object.values(serverErrors(error)).flat()
  if (messages.length > 0) return messages.join(', ')
  if (axios.isAxiosError(error) && error.response?.statusText) return error.response.statusText
  return t('catalogue.request_failed')
}

export const deleteErrorMessage = (error: unknown): string => errorBody(error).base?.join(', ') ?? errorMessage(error)
